import { callStreamingEdgeFunction } from './edgeFunctionClient';
import type { EdgeFunctionRequest, StreamingEdgeFunctionResult, StreamingOptions, ReviewProgress } from './edgeFunctionClient';
import {
  buildSectionTracker,
  TENANCY_EXPECTED_SECTIONS,
  COMMERCIAL_EXPECTED_SECTIONS,
  TENANCY_EXECUTION_FALLBACK,
  COMMERCIAL_EXECUTION_FALLBACK,
} from '../utils/documentPostProcessor';

const MAX_CONTINUATIONS = 10;
const CONTINUE_MARKER = '[CONTINUE]';
const CONTEXT_TAIL_CHARS = 20000;
const PROGRESS_DENOMINATOR = 250000;
const SAFETY_MAX_CHARS = 200000;
const SAFETY_MIN_PARTS = 3;

const SIGNATURE_PATTERNS = [
  /signed\s+by/i,
  /\bexecution\b.*\bsection\b/i,
  /\bin\s+witness\s+whereof\b/i,
  /\bsignature\s*:/i,
  /\blandlord\s+signature\b/i,
  /\btenant\s+signature\b/i,
  /\bexecuted\s+as\s+a\s+deed\b/i,
  /\bsigned\s+sealed\s+and\s+delivered\b/i,
  /\bdate\s+of\s+signature\b/i,
  /\bwitness\s+name\b/i,
  /\b##\s*EXECUTION\b/i,
];

function isDocumentComplete(text: string): boolean {
  const tail = text.slice(-3000);
  return SIGNATURE_PATTERNS.some((pattern) => pattern.test(tail));
}

export type DocumentType = 'tenancy' | 'commercial';

interface RewriteWithContinuationOptions {
  baseRequest: EdgeFunctionRequest;
  onChunk: (chunk: string) => void;
  onProgress?: (progress: ReviewProgress) => void;
  signal?: AbortSignal;
  streamingOptions?: StreamingOptions;
  progressMessage: string;
  documentType?: DocumentType;
}

export async function rewriteWithContinuation({
  baseRequest,
  onChunk,
  onProgress,
  signal,
  streamingOptions,
  progressMessage,
  documentType = 'tenancy',
}: RewriteWithContinuationOptions): Promise<StreamingEdgeFunctionResult> {
  let accumulated = '';
  let totalChars = 0;

  const expectedSections = documentType === 'commercial'
    ? COMMERCIAL_EXPECTED_SECTIONS
    : TENANCY_EXPECTED_SECTIONS;

  const firstResult = await callStreamingEdgeFunction(
    baseRequest,
    (chunk) => {
      totalChars += chunk.length;
      accumulated += chunk;
      onChunk(chunk);
      const ratio = Math.min(totalChars / PROGRESS_DENOMINATOR, 1);
      onProgress?.({
        status: 'rewriting',
        stage: 2,
        totalStages: 4,
        message: progressMessage,
        percent: Math.round(26 + ratio * 49),
        streamedChars: totalChars,
      });
    },
    signal,
    streamingOptions,
  );

  accumulated = firstResult.content;
  let stopReason = firstResult.stopReason;

  for (let part = 1; part <= MAX_CONTINUATIONS; part++) {
    const textEndsContinue = accumulated.trimEnd().endsWith(CONTINUE_MARKER);
    const hitMaxTokens = stopReason === 'max_tokens';
    const docComplete = isDocumentComplete(accumulated);

    if (!hitMaxTokens && !textEndsContinue && docComplete) break;

    if (!hitMaxTokens && !textEndsContinue) {
      if (part >= SAFETY_MIN_PARTS && accumulated.length > SAFETY_MAX_CHARS) break;
    }

    if (signal?.aborted) break;

    const cleanedContext = accumulated.replace(new RegExp(`\\s*\\${CONTINUE_MARKER}\\s*$`), '');
    const contextTail = cleanedContext.slice(-CONTEXT_TAIL_CHARS);

    const tracker = buildSectionTracker(cleanedContext, expectedSections);

    onProgress?.({
      status: 'rewriting',
      stage: 2,
      totalStages: 4,
      message: `Completing document rewrite (part ${part + 1} of up to ${MAX_CONTINUATIONS + 1})...`,
      percent: Math.round(26 + Math.min(totalChars / PROGRESS_DENOMINATOR, 1) * 49),
      streamedChars: totalChars,
    });

    const continuationResult = await callStreamingEdgeFunction(
      {
        ...baseRequest,
        continuationContext: contextTail,
        partIndex: part,
        sectionTracker: tracker,
      },
      (chunk) => {
        totalChars += chunk.length;
        onChunk(chunk);
        const ratio = Math.min(totalChars / PROGRESS_DENOMINATOR, 1);
        onProgress?.({
          status: 'rewriting',
          stage: 2,
          totalStages: 4,
          message: `Completing document rewrite (part ${part + 1} of up to ${MAX_CONTINUATIONS + 1})...`,
          percent: Math.round(26 + ratio * 49),
          streamedChars: totalChars,
        });
      },
      signal,
      streamingOptions,
    );

    accumulated = cleanedContext + continuationResult.content;
    stopReason = continuationResult.stopReason;
  }

  let finalContent = accumulated.replace(new RegExp(`\\s*\\${CONTINUE_MARKER}\\s*$`), '');

  if (!isDocumentComplete(finalContent)) {
    const fallback = documentType === 'commercial'
      ? COMMERCIAL_EXECUTION_FALLBACK
      : TENANCY_EXECUTION_FALLBACK;
    finalContent += fallback;
  }

  return { content: finalContent, stopReason };
}
